import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { History, Plus } from "lucide-react";

import CodeBlock from "./CodeBlock";
import { CodeEditorRef } from "../canvas/CodeEditor";
import PromptInput from "../ui/PromptInput";
import React from "react";
import { cn } from "../../lib/utils";
import { filesystemService } from "../../utils/filesystem";
import { pyodideService } from "@/services/PyodideService";
import { rootStore } from "../../stores/RootStore";
import { useCodeGeneration } from "../../hooks/useCodeGeneration";

interface Message {
    role: 'user' | 'assistant';
    content: string;
    generatedCode?: string;
    isCodeApplied?: boolean;
    planningSteps?: PlanningStep[];
    isTyping?: boolean;
}

interface PlanningStep {
    phase: string;
    description?: string;
    status: 'pending' | 'in-progress' | 'completed' | 'error';
    details?: string;
    isTyping?: boolean;
    fullText?: string;
}

interface AgentPanelProps {
    className?: string;
    selectedFile: string | null;
    codeEditorRef: React.RefObject<CodeEditorRef>;
}

interface EmptyStateProps {
    onPromptSelect: (prompt: string) => void;
}

const EmptyState: FC<EmptyStateProps> = ({ onPromptSelect }) => {
    return (
        <div className="flex flex-col items-center justify-center h-full px-6 py-10 space-y-6">
            <div className="w-34 h-34 opacity-20">
                <img
                    src="/symbol.png"
                    alt="Waldo"
                    className="w-full h-full object-contain"
                    onClick={() => onPromptSelect("Create a simple data visualization")}
                />
            </div>
        </div>
    );
};

const AnimatedEllipses = () => {
    const [dots, setDots] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setDots(prev => prev.length >= 3 ? '' : prev + '.');
        }, 500);

        return () => clearInterval(interval);
    }, []);

    return <span className="text-gray-400">{dots}</span>;
};

const AgentPanel: FC<AgentPanelProps> = ({
    className,
    selectedFile,
    codeEditorRef
}) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedModel, setSelectedModel] = useState<string>("gpt-4o-mini");
    const [hasProcessedInitialPrompt, setHasProcessedInitialPrompt] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { generateCode: generateCodeAPI } = useCodeGeneration();
    const { uiStore } = rootStore;

    const modelOptions = [
        { name: "GPT-o4 Mini", value: "o4-mini-2025-04-16", provider: "openai", providerTitle: "Open AI" },
        { name: "GPT-4o", value: "gpt-4o", provider: "openai", providerTitle: "Open AI" },
        { name: "Claude 3.7 Sonnet", value: "claude-3-7-sonnet-20250219", provider: "anthropic", providerTitle: "Anthropic" },
        { name: "Claude 3.5 Haiku", value: "claude-3-5-haiku-20241022", provider: "anthropic", providerTitle: "Anthropic" }
    ];

    const currentModel = useMemo(() =>
        modelOptions.find(m => m.value === selectedModel) || modelOptions[0],
        [selectedModel, modelOptions]
    );

    // Load saved model preference on mount
    useEffect(() => {
        const savedModel = localStorage.getItem("webwald-selected-model");
        if (savedModel && modelOptions.find(m => m.value === savedModel)) {
            setSelectedModel(savedModel);
        }
    }, []);

    useEffect(() => {
        localStorage.setItem("webwald-selected-model", selectedModel);
    }, [selectedModel]);

    // Memoize the pyodidePath to avoid recalculating it on every render
    const pyodidePath = useMemo(() => {
        if (!selectedFile) return null;
        return selectedFile.startsWith('/') ? selectedFile : `/project/${selectedFile}`;
    }, [selectedFile]);

    // Memoize the scroll to bottom function
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Add event listener for custom agent messages
    useEffect(() => {
        const handleAgentMessage = (event: CustomEvent) => {
            const { role, content, planningSteps } = event.detail;
            setMessages(prev => [...prev, { role, content, planningSteps }]);
        };

        window.addEventListener('addAgentMessage', handleAgentMessage as EventListener);

        return () => {
            window.removeEventListener('addAgentMessage', handleAgentMessage as EventListener);
        };
    }, []);

    // Add event listener for planning step updates
    useEffect(() => {
        const handlePlanningStepUpdate = (event: CustomEvent) => {
            const { messageIndex, stepIndex, status, details } = event.detail;
            setMessages(prev => prev.map((msg, idx) => {
                if (idx === messageIndex && msg.planningSteps) {
                    const updatedSteps = [...msg.planningSteps];
                    updatedSteps[stepIndex] = {
                        ...updatedSteps[stepIndex],
                        status,
                        ...(details && { details })
                    };
                    return { ...msg, planningSteps: updatedSteps };
                }
                return msg;
            }));
        };

        window.addEventListener('updatePlanningStep', handlePlanningStepUpdate as EventListener);

        return () => {
            window.removeEventListener('updatePlanningStep', handlePlanningStepUpdate as EventListener);
        };
    }, []);

    // Function to generate code using OpenAI
    const generateCode = useCallback(async (
        prompt: string
    ): Promise<{ generatedCode: string, updatedContent: string } | null> => {
        try {
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            // If no file is selected, we'll create a new one
            let currentContent = "";
            if (selectedFile && pyodidePath) {
                currentContent = await filesystemService.readFileFromPyodide(pyodidePath);
            }

            // Add preswald display context to the prompt
            const displayContext = `
# Displays
import preswald

preswald.text("# Title")
preswald.text("## Subtitle")
preswald.text("Description")
preswald.plotly(fig)
`;
            const enhancedPrompt = `${prompt}\n\nUse the following display functions to show content in the app:\n${displayContext}`;

            const generatedCode = await generateCodeAPI({
                userRequest: enhancedPrompt,
                currentContent,
                dataContext: "",
                provider: currentModel.provider as "openai" | "anthropic",
                model: currentModel.value,
                signal,
            });

            if (generatedCode) {
                return {
                    generatedCode: generatedCode,
                    updatedContent: currentContent
                };
            } else {
                console.error("Failed to generate code. Please check your OpenAI API key.");
                return null;
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('Code generation was cancelled');
                return null;
            }
            console.error("Error generating code:", error);
            return null;
        } finally {
            abortControllerRef.current = null;
        }
    }, [selectedFile, pyodidePath, generateCodeAPI, currentModel.provider, currentModel.value]);

    // Function to apply the generated code
    const applyCode = useCallback(async (code: string): Promise<boolean> => {
        if (!selectedFile || !pyodidePath) return false;

        try {
            // Write the code to the file
            await filesystemService.writeFileToPyodide(pyodidePath, code);

            // Update the editor content
            if (codeEditorRef.current) {
                await codeEditorRef.current.refresh();
            }

            // Dispatch file change event
            window.dispatchEvent(new CustomEvent('filesystem:file-changed', {
                detail: { path: pyodidePath }
            }));

            // Update message state to show code was applied
            setMessages(prev => prev.map(msg => {
                if (msg.generatedCode === code) {
                    return { ...msg, isCodeApplied: true };
                }
                return msg;
            }));

            return true;
        } catch (error) {
            console.error("Error applying code:", error);
            uiStore.updateLogs(`Error applying code: ${error instanceof Error ? error.message : String(error)}`);
            return false;
        }
    }, [selectedFile, pyodidePath, codeEditorRef, uiStore]);

    // Function to handle applying the generated code
    const handleApplyCode = useCallback(async (generatedCode: string, messageIndex: number) => {
        setIsLoading(true);
        try {
            const success = await applyCode(generatedCode);
            if (success) {
                setMessages(prev => prev.map((msg, idx) =>
                    idx === messageIndex
                        ? { ...msg, isCodeApplied: true }
                        : msg
                ));
            }
        } catch (error) {
            console.error('Error applying code:', error);
        } finally {
            setIsLoading(false);
        }
    }, [applyCode]);

    // Handle rejection of code suggestions
    const handleRejectCode = useCallback((messageIndex: number) => {
        setMessages(prev => prev.filter((_, index) => index !== messageIndex));
    }, []);

    // Memoize the message content formatter
    const formatMessageContent = useCallback((content: string, generatedCode?: string, messageIndex?: number, isCodeApplied?: boolean, planningSteps?: PlanningStep[]) => {
        if (planningSteps && planningSteps.length > 0) {
            return (
                <div className="flex flex-col gap-1">
                    {planningSteps.map((step, idx) => (
                        <div key={idx} className="flex items-start gap-1.5">
                            <span className="text-xs text-gray-400 mt-0.5">•</span>
                            <span className={cn(
                                "text-xs",
                                step.status === 'completed' ? "text-gray-500" : "text-gray-400"
                            )}>
                                {step.phase}
                                {step.isTyping && step.status === 'in-progress' && (
                                    <span className="inline-block w-1 h-3 bg-gray-400 animate-pulse ml-0.5" />
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (generatedCode) {
            const message = messages[messageIndex || 0];
            const isTyping = (message as any)?.isTyping || false;

            return (
                <CodeBlock
                    code={generatedCode}
                    language="python"
                    showApplyButton={!isCodeApplied}
                    showRejectButton={!isCodeApplied}
                    onApply={() => messageIndex !== undefined && handleApplyCode(generatedCode, messageIndex)}
                    onReject={() => messageIndex !== undefined && handleRejectCode(messageIndex)}
                    isApplied={isCodeApplied}
                    isTyping={isTyping}
                />
            );
        }

        return <span>{content}</span>;
    }, [handleApplyCode, handleRejectCode, isLoading, messages]);

    // Memoize the message list component
    const MessageList = useMemo(() => (
        <div className="p-2 space-y-2">
            {messages.map((message, index) => (
                <div
                    key={index}
                    className="flex flex-col w-full"
                >
                    <div className={cn(
                        "w-full text-xs leading-snug",
                        message.role === 'user'
                            ? "text-gray-900"
                            : "text-gray-400"
                    )}>
                        {formatMessageContent(message.content, message.generatedCode, index, message.isCodeApplied, message.planningSteps)}
                    </div>
                </div>
            ))}
            {isLoading && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <div className="w-3 h-3 animate-spin">
                        <div className="w-full h-full border-2 border-gray-300 border-t-gray-600 rounded-full" />
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    ), [messages, isLoading, formatMessageContent]);

    // Function to handle stopping the generation
    const handleStopGeneration = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
        setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'Code generation was cancelled.'
        }]);
    }, []);

    const handleSubmit = useCallback(async (prompt: string) => {
        if (!prompt || isLoading) return;

        console.log('Handling submit with message:', prompt);
        setMessages(prev => [...prev, { role: 'user', content: prompt }]);
        setIsLoading(true);

        try {
            if (!selectedFile || !pyodidePath) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'No file selected. Please select a file first by clicking on it in the sidebar.'
                }]);
                setIsLoading(false);
                return;
            }

            const currentContent = await filesystemService.readFileFromPyodide(pyodidePath);

            // Use real-time streaming for immediate updates
            console.log('Starting real-time streaming API call');

            // Create state tracking for streaming
            let planningMessageIndex = -1;
            let codeMessageIndex = -1;
            let accumulatedCode = '';
            let planningSteps: PlanningStep[] = [];
            let currentPlanningText = ['', '', '', '']; // Track text for up to 4 steps
            const isFirstGeneration = messages.length === 0; // Track if this is the first generation

            // Create abort controller for cleanup
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            // Add preswald display context to the prompt
            const displayContext = `
# Displays
import preswald 
preswald.text("# Title")
preswald.text("## Subtitle")
preswald.text("Description")
preswald.plotly(fig)
`;
            const enhancedPrompt = `${prompt}\n\nUse the following display functions to show content in the app:\n${displayContext}`;

            // Send streaming POST request
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/generate-code`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                signal,
                body: JSON.stringify({
                    user_request: enhancedPrompt,
                    current_content: currentContent,
                    data_context: "",
                    provider: currentModel.provider,
                    model: currentModel.value
                }),
            });

            if (!response.ok) {
                const errorJson = await response.json().catch(() => ({}));
                throw new Error(errorJson.message || "Failed to generate response");
            }

            // Parse the streaming response
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error("Failed to get response reader");
            }

            let buffer = '';

            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');

                    // Keep the last incomplete line in the buffer
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.substring(6));

                                switch (data.type) {
                                    case 'start':
                                        console.log('Real-time stream started');
                                        break;

                                    case 'planning_start':
                                        // Create planning message immediately
                                        setMessages(prev => {
                                            const newMessages = [...prev, {
                                                role: 'assistant' as const,
                                                content: '',
                                                planningSteps: []
                                            }];
                                            planningMessageIndex = newMessages.length - 1;
                                            return newMessages;
                                        });
                                        // Reset planning state
                                        planningSteps = [];
                                        currentPlanningText = ['', '', '', ''];
                                        break;

                                    case 'planning_step_start':
                                        // Initialize a new planning step
                                        if (typeof data.index === 'number') {
                                            const stepIndex = data.index;
                                            currentPlanningText[stepIndex] = '';

                                            // Ensure we have a step at this index
                                            if (!planningSteps[stepIndex]) {
                                                planningSteps[stepIndex] = {
                                                    phase: '',
                                                    status: 'in-progress',
                                                    isTyping: true
                                                };
                                            }

                                            // Update messages with the current planning steps
                                            setMessages(prev => prev.map((msg, idx) =>
                                                idx === planningMessageIndex
                                                    ? {
                                                        ...msg,
                                                        planningSteps: planningSteps.slice(0, stepIndex + 1).filter(step => step !== undefined)
                                                    }
                                                    : msg
                                            ));
                                        }
                                        break;

                                    case 'planning_chunk':
                                        // Stream planning text character by character
                                        if (data.data && typeof data.index === 'number') {
                                            const stepIndex = data.index;

                                            // Accumulate text for this step
                                            currentPlanningText[stepIndex] = (currentPlanningText[stepIndex] || '') + data.data;

                                            // Ensure we have a step at this index
                                            if (!planningSteps[stepIndex]) {
                                                planningSteps[stepIndex] = {
                                                    phase: '',
                                                    status: 'in-progress',
                                                    isTyping: true
                                                };
                                            }

                                            // Update the step's phase with accumulated text
                                            planningSteps[stepIndex] = {
                                                ...planningSteps[stepIndex],
                                                phase: currentPlanningText[stepIndex],
                                                status: 'in-progress',
                                                isTyping: true
                                            };

                                            // Update messages with the current planning steps
                                            setMessages(prev => prev.map((msg, idx) =>
                                                idx === planningMessageIndex
                                                    ? {
                                                        ...msg,
                                                        planningSteps: planningSteps.slice(0, stepIndex + 1).filter(step => step !== undefined)
                                                    }
                                                    : msg
                                            ));
                                        }
                                        break;

                                    case 'planning_step_complete':
                                        // Mark step as completed
                                        if (typeof data.index === 'number' && planningSteps[data.index]) {
                                            planningSteps[data.index].status = 'completed';
                                            planningSteps[data.index].isTyping = false;

                                            setMessages(prev => prev.map((msg, idx) =>
                                                idx === planningMessageIndex
                                                    ? { ...msg, planningSteps: [...planningSteps] }
                                                    : msg
                                            ));
                                        }
                                        break;

                                    case 'planning_complete':
                                        console.log('Planning phase completed');
                                        break;

                                    case 'code_start':
                                        // Create code message immediately
                                        setMessages(prev => {
                                            const newMessages = [...prev, {
                                                role: 'assistant' as const,
                                                content: '',
                                                generatedCode: '',
                                                isTyping: true
                                            }];
                                            codeMessageIndex = newMessages.length - 1;
                                            return newMessages;
                                        });
                                        accumulatedCode = '';
                                        break;

                                    case 'code_chunk':
                                        // Append code chunk in real-time
                                        if (data.data) {
                                            accumulatedCode += data.data;
                                            let displayCode = accumulatedCode;

                                            // Don't clean code while streaming, show raw content
                                            // Only remove the opening ```python if it's there
                                            if (displayCode.startsWith('```python\n')) {
                                                displayCode = displayCode.substring(10);
                                            }
                                            // Remove closing ``` if present
                                            if (displayCode.endsWith('```')) {
                                                displayCode = displayCode.substring(0, displayCode.length - 3);
                                            }

                                            setMessages(prev => prev.map((msg, idx) =>
                                                idx === codeMessageIndex
                                                    ? { ...msg, generatedCode: displayCode }
                                                    : msg
                                            ));
                                        }
                                        break;

                                    case 'code_complete':
                                        // Finalize code
                                        if (data.data) {
                                            let finalCode = data.data.replace(/^python\n/, '');
                                            finalCode = finalCode.replace(/^```python\n/, '').replace(/```$/, '');

                                            setMessages(prev => prev.map((msg, idx) =>
                                                idx === codeMessageIndex
                                                    ? { ...msg, generatedCode: finalCode, isTyping: false }
                                                    : msg
                                            ));

                                            // Auto-apply the code if this is the first generation
                                            if (isFirstGeneration) {
                                                await applyCode(finalCode);
                                                setMessages(prev => prev.map((msg, idx) =>
                                                    idx === codeMessageIndex
                                                        ? { ...msg, isCodeApplied: true }
                                                        : msg
                                                ));
                                            }
                                        }
                                        console.log('Code generation completed');
                                        break;

                                    case 'end':
                                    case 'stream_end':
                                        console.log('Stream ended successfully');
                                        setIsLoading(false);
                                        break;

                                    case 'error':
                                        console.error('Stream error:', data.message);
                                        setMessages(prev => [...prev, {
                                            role: 'assistant',
                                            content: `An error occurred: ${data.message}`
                                        }]);
                                        setIsLoading(false);
                                        break;
                                }
                            } catch (e) {
                                console.error('Error parsing stream data:', e, 'Line:', line);
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }

        } catch (error: unknown) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('Code generation was cancelled');
                return;
            }
            console.error('Error in handleSubmit:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'An error occurred while processing your request. Please try again.'
            }]);
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    }, [isLoading, selectedFile, pyodidePath, currentModel.provider, currentModel.value, messages.length, applyCode]);

    const handleNewChat = useCallback(() => {
        setMessages([]);
    }, []);

    useEffect(() => {
        const processInitialPrompt = async () => {
            if (uiStore.initialAgentPrompt && !hasProcessedInitialPrompt && selectedFile && !isLoading) {
                const promptToProcess = uiStore.initialAgentPrompt;

                uiStore.initialAgentPrompt = null;
                setHasProcessedInitialPrompt(true);

                await handleSubmit(promptToProcess);
            }
        };

        processInitialPrompt();
    }, [uiStore.initialAgentPrompt, hasProcessedInitialPrompt, selectedFile, isLoading, handleSubmit]);

    return (
        <div
            className={cn(
                "border-l border-gray-200 bg-white flex flex-col h-full relative",
                className
            )}
        >
            <div className="flex-none p-1.5 bg-white">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900"></span>
                    <div className="flex items-center gap-1">
                        <button
                            className="flex items-center justify-center w-5 h-5 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                            title="New conversation"
                            onClick={handleNewChat}
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                        <button
                            className="flex items-center justify-center w-5 h-5 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                            title="View history"
                        >
                            <History className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </div>

            {messages.length === 0 ? (
                <div className="flex flex-col h-full">
                    <EmptyState onPromptSelect={handleSubmit} />
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-full max-w-md">
                            <PromptInput
                                onSubmit={handleSubmit}
                                isLoading={isLoading}
                                onStopGeneration={handleStopGeneration}
                                selectedModel={selectedModel}
                                onModelSelect={setSelectedModel}
                                modelOptions={modelOptions}
                                borderColor="border-gray-200"
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {MessageList}
                    </div>
                    <PromptInput
                        onSubmit={handleSubmit}
                        isLoading={isLoading}
                        onStopGeneration={handleStopGeneration}
                        selectedModel={selectedModel}
                        onModelSelect={setSelectedModel}
                        modelOptions={modelOptions}
                        borderColor="border-gray-200"
                    />
                </>
            )}
        </div>
    );
};

// Export a memoized version of the component
export default React.memo(AgentPanel);